import { useEffect, useState } from "react";

/**
 * Public site-settings loader — Phase 6.1 + Sprint 36.
 * Reads `/api/settings/public` (whitelisted keys only). Falls back to
 * built-in defaults if the request fails so the marketing hero never
 * blocks on a network hiccup.
 *
 * Sprint 36 extends the shape with typography enums, section
 * visibility booleans, order integers, an announcement banner, and
 * the UAE market status flag. Consumers can also call
 * `applyTypographyVars(settings)` to set CSS variables on <html>.
 */
const DEFAULTS = {
  hero_title: "فُرصلوجيا · الذكاء الاقتصادي المستقل",
  hero_subtitle: "منصّة استخبارات اقتصادية آنية تكتشف الفرص من المصادر الرسميّة تلقائياً.",
  hero_cta_primary: "استكشف الفرص",
  hero_cta_secondary: "كيف نعمل",
  footer_tagline: "استخبارات اقتصادية مبنية على المصادر الرسميّة.",
  // Sprint 36 · homepage section copy
  section_kpi_title: "",
  section_kpi_description: "",
  section_opportunities_title: "",
  section_opportunities_description: "",
  section_knowledge_title: "",
  section_knowledge_description: "",
  // Sprint 36 · typography (enum values only)
  typography_font_family: "cairo",
  typography_hero_size: "lg",
  typography_section_size: "md",
  typography_body_size: "md",
  // Sprint 36 · section visibility + order
  section_kpi_visible: "true",
  section_kpi_order: "10",
  section_opportunities_visible: "true",
  section_opportunities_order: "20",
  section_knowledge_visible: "true",
  section_knowledge_order: "30",
  // Sprint 36 · announcement banner
  announcement_enabled: "false",
  announcement_title: "",
  announcement_body: "",
  announcement_tone: "info",
  // Sprint 36 · market status
  market_ae_status: "proof",
};

let _cache = null;
let _inflight = null;

export const useSiteSettings = () => {
  const [settings, setSettings] = useState(_cache || DEFAULTS);
  useEffect(() => {
    if (_cache) return;
    if (_inflight) { _inflight.then((d) => setSettings(d)); return; }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    _inflight = fetch(`${base}/api/settings/public`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        _cache = { ...DEFAULTS, ...d };
        return _cache;
      })
      .catch(() => {
        _cache = DEFAULTS;
        return _cache;
      });
    _inflight.then((d) => setSettings(d));
  }, []);
  return settings;
};

// ── Sprint 36 · typography CSS variable application ──────────────
// Applied on <html> so the whole app inherits. Enum values map to
// responsive clamp() ranges so the same var stays fluid across
// breakpoints — small screens shrink automatically, extreme values
// are bounded by the min/max.

const HERO_SIZE_CLAMP = {
  sm: "clamp(1.5rem, 1.15rem + 1.8vw, 2.25rem)",
  md: "clamp(1.75rem, 1.25rem + 2.4vw, 2.75rem)",
  lg: "clamp(1.9rem, 1.15rem + 3.4vw, 3.25rem)",
  xl: "clamp(2.25rem, 1.35rem + 4.3vw, 4rem)",
};
const SECTION_SIZE_CLAMP = {
  sm: "clamp(1.05rem, 0.95rem + 0.6vw, 1.25rem)",
  md: "clamp(1.25rem, 1rem + 1.1vw, 1.875rem)",
  lg: "clamp(1.4rem, 1rem + 1.6vw, 2.25rem)",
  xl: "clamp(1.6rem, 1.05rem + 2.2vw, 2.75rem)",
};
const BODY_SIZE_CLAMP = {
  sm: "clamp(0.8125rem, 0.78rem + 0.15vw, 0.9375rem)",
  md: "clamp(0.9375rem, 0.9rem + 0.3vw, 1.0625rem)",
  lg: "clamp(1.0625rem, 1rem + 0.35vw, 1.1875rem)",
};
const FONT_FAMILY_STACK = {
  cairo:           "'Cairo', 'IBM Plex Sans Arabic', system-ui, sans-serif",
  tajawal:         "'Tajawal', 'IBM Plex Sans Arabic', system-ui, sans-serif",
  ibm_plex_arabic: "'IBM Plex Sans Arabic', system-ui, sans-serif",
};

export const applyTypographyVars = (settings) => {
  if (typeof document === "undefined" || !settings) return;
  const root = document.documentElement;
  const hero    = HERO_SIZE_CLAMP[settings.typography_hero_size]      || HERO_SIZE_CLAMP.lg;
  const section = SECTION_SIZE_CLAMP[settings.typography_section_size] || SECTION_SIZE_CLAMP.md;
  const body    = BODY_SIZE_CLAMP[settings.typography_body_size]       || BODY_SIZE_CLAMP.md;
  const family  = FONT_FAMILY_STACK[settings.typography_font_family]   || FONT_FAMILY_STACK.ibm_plex_arabic;
  root.style.setProperty("--fursa-typo-hero", hero);
  root.style.setProperty("--fursa-typo-section", section);
  root.style.setProperty("--fursa-typo-body", body);
  root.style.setProperty("--fursa-typo-family", family);
};

export const useApplyTypography = () => {
  const settings = useSiteSettings();
  useEffect(() => { applyTypographyVars(settings); }, [
    settings.typography_hero_size,
    settings.typography_section_size,
    settings.typography_body_size,
    settings.typography_font_family,
  ]);
  return settings;
};

export default useSiteSettings;
